import { useRef, useCallback } from 'react'
import { apiStream } from '@/shared/lib/apiClient'
import { useStoryWriterStore } from '../store/storyWriterStore'
import { STORY_WRITER_SYSTEM_PROMPT } from '../lib/defaultPrompt'

interface SSEEvent {
  type: 'token' | 'done' | 'error'
  value?: string
  inputTokens?: number
  outputTokens?: number
  message?: string
}

export function useGenerateStories() {
  const abortRef = useRef<AbortController | null>(null)
  const {
    inputText,
    setStatus,
    appendOutputText,
    setOutputText,
    setError,
    setTokenUsage,
  } = useStoryWriterStore()

  const generate = useCallback(async () => {
    if (!inputText.trim()) return

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setOutputText('')
    setStatus('streaming')
    console.log('[StoryWriter] Starting generation', { inputLength: inputText.length })

    try {
      const reader = await apiStream(
        '/story-writer',
        { systemPrompt: STORY_WRITER_SYSTEM_PROMPT, userContent: inputText },
        controller.signal
      )

      const decoder = new TextDecoder()
      let buffer = ''

      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        buffer += decoder.decode(value, { stream: true })
        const lines = buffer.split('\n\n')
        buffer = lines.pop() ?? ''

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue
          const raw = line.slice(6).trim()
          if (!raw) continue

          try {
            const event = JSON.parse(raw) as SSEEvent
            if (event.type === 'token' && event.value) {
              appendOutputText(event.value)
            } else if (event.type === 'done') {
              console.log('[StoryWriter] Stream complete', { inputTokens: event.inputTokens, outputTokens: event.outputTokens })
              setStatus('done')
              if (event.inputTokens !== undefined && event.outputTokens !== undefined) {
                setTokenUsage(event.inputTokens, event.outputTokens)
              }
            } else if (event.type === 'error') {
              console.error('[StoryWriter] Stream error', event.message)
              setError(event.message ?? 'Unknown error')
            }
          } catch {
            // Malformed SSE line — skip
          }
        }
      }
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        console.log('[StoryWriter] Request aborted by user')
        setStatus('aborted')
      } else {
        console.error('[StoryWriter] Request failed', err)
        setError(err instanceof Error ? err.message : 'Request failed')
      }
    }
  }, [inputText, setStatus, setOutputText, appendOutputText, setError, setTokenUsage])

  const abort = useCallback(() => {
    abortRef.current?.abort()
    setStatus('aborted')
  }, [setStatus])

  return { generate, abort }
}
